import {Component, OnInit} from '@angular/core';
import {StepComponent} from "../../step/step.component";
import {BestandsaufnahmeForm} from "./bestandsaufnahme.form";
import {Step, StepService} from "../../services/step.service";
import {Router} from "@angular/router";
import {ModeratRequestService} from "../services/request.service";
import {ModeratAnfrage} from "../anfrage/moderat.anfrage";
import {ElementService} from "../../services/element.service";
import {PortalService} from "../../services/portal.service";
import {StepNavigationElement} from "../../step/step.navigation.element";
import {WpcheckStartComponent} from "../wpcheck/wpcheck.start.component";
import {VerbrauchsanalyseModeratForm} from "../verbrauchsanalyse/verbrauchsanalyse.moderat.form";
import {FormConfig} from "../../form/form.config";
import {ValidationService} from "../../services/validation.service";
import {EnergyMapService} from "../../energy-map/services/energy.map.service";
import {ModeratConstants} from "../moderat.constants";

/**
 * Overwrite the Generic step component for the first section fo the moderat.
 *
 * @inheritDoc
 */
@Component({
  selector: 'app-step-bestandsaufnahme',
  templateUrl: '../../step/step.component.html'
})
export class BestandsaufnahmeStepComponent extends StepComponent implements OnInit{

  /**
   * (input) data of the user
   */
  anfrage: ModeratAnfrage;

  /**
   * @inheritDoc
   */
  formConfig: BestandsaufnahmeForm;

  /**
   * Overwrite the label prefix.
   */
  textkeyPrefix: string = "moderat";

  /**
   * @inheritDoc
   * @param stepService
   * @param router
   * @param requestService
   * @param elementService
   * @param portalService
   * @param energyMapService
   */
  constructor(protected stepService: StepService, protected router: Router, protected requestService: ModeratRequestService,
              protected elementService: ElementService, protected portalService: PortalService,
              protected energyMapService: EnergyMapService) {
    super(stepService, router, elementService);
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // add the correct form config that we need in these steps and init the parent form
    this.formConfig = new BestandsaufnahmeForm();
    this.subscriptions.push(
      this.formConfig.fieldChange.subscribe(() => this.setAutoSubmitAndInlineHelp())
    );
    super.ngOnInit();

    // special case, last step on this chapter
    if(this.steps.currentStep.name === 'daemmung/3' && this.steps.nextStep === null) {
      this.steps.nextStep = new Step('sanierungmassnahmen', '/moderat/sanierung/massnahmen');
    }

    // get the current input data and preset it. We set date from first AND second chapter here because there could be dependencies
    this.anfrage = this.requestService.getAnfrage();
    this.formConfig.anfrage = this.anfrage;
    this.formConfig.setData(this.anfrage.verbrauchsanalyseInput);

    // in some case we have to preset fiedls, based on the input from first section
    switch(this.anfrage.verbrauchsanalyseInput.energietraeger_name) {
      case 'holzpellets':
        this.anfrage.bestandsanalyseInput.heizsystem_typ = 'holzpelletsheizkessel';
        break;
      case 'fernwaerme':
        this.anfrage.bestandsanalyseInput.heizsystem_typ = 'fernwaerme_uebergabestation';
        break;
      case 'strom':
      case 'waermepumpe':
      case 'waermepumpe_luft':
      case 'waermepumpe_wasser':
        this.anfrage.bestandsanalyseInput.heizsystem_typ = this.anfrage.verbrauchsanalyseInput.energietraeger_name;
        break;
      case 'erdgas':
      case 'heizoel':
      case 'fluessiggas':
        if(['holzpelletsheizkessel', 'fernwaerme_uebergabestation'].indexOf(this.anfrage.bestandsanalyseInput.heizsystem_typ) > -1) {
          // reset the value because this is wrong here and will be asked later
          this.anfrage.bestandsanalyseInput.heizsystem_typ = null;
        }
        break;
      default:
        this.anfrage.bestandsanalyseInput.heizsystem_typ = 'niedertemperaturkessel';
        break;
    }
    if(this.anfrage.verbrauchsanalyseInput.keller_status === 'nicht_vorhanden') {
      this.anfrage.bestandsanalyseInput.kellerdecke_zustand = null;
      this.anfrage.bestandsanalyseInput.kellerdecke_baujahr = null;
    }
    if(this.anfrage.verbrauchsanalyseInput.solarthermie_art === 'nicht_vorhanden') {
      this.anfrage.bestandsanalyseInput.solarthermie_zustand = null;
      this.anfrage.bestandsanalyseInput.solarthermie_baujahr = null;
    }
    if((this.anfrage.verbrauchsanalyseInput.solarthermie_art === 'warmwasser' || this.anfrage.verbrauchsanalyseInput.solarthermie_art === 'warmwasser_heizung') &&
      this.anfrage.verbrauchsanalyseInput.gebaeudebaujahr < ModeratConstants.FORM_SOLAR_THERMAL_BUILDING_YEAR_MIN) {
      const solarthermieZustand = this.formConfig.getField('solarthermie_zustand');
      if(solarthermieZustand) {
        solarthermieZustand.setOptionsDisabled(['nicht_erneuert']);
      }
      if(this.anfrage.bestandsanalyseInput.solarthermie_zustand === 'nicht_erneuert') {
        this.anfrage.bestandsanalyseInput.solarthermie_zustand = null;
      }
    }

    // preset values from second chapter
    this.formConfig.setData(this.anfrage.bestandsanalyseInput);

    // update autocommit because the active fields could have been changed
    this.setAutoSubmitAndInlineHelp();

    // update the previous step
    this.onStepUpdate();

    // show navigation element back to the wpcheck
    if (this.anfrage.mode === ModeratAnfrage.MODE_WPCHECK) {
      this.stepService.setNavigationPreElement(
        new StepNavigationElement('moderat.nav.wpcheck.', true,
          WpcheckStartComponent.wpcheckUrl.replace('{{portalId}}', this.portalService.currentPortalId))
      );
      // redirect to the next step if kessel_brennwerttechnik is inactive
      if (this.steps.currentStep.name === 'wpcheizanlage/1' && this.formConfig.getField('kessel_brennwerttechnik').isActive() === false) {
        this.router.navigateByUrl(this.steps.nextStep.path);
      }
    }
  }

  /**
   * @inheritDoc
   * @param data
   */
  saveData(data: {[key: string]: any}) {
    // merge the data with the existing data
    this.anfrage.bestandsanalyseInput = {...this.anfrage.bestandsanalyseInput, ...data};
    this.requestService.setAnfrage(this.anfrage);

    // save the data also to the server
    this.requestService.saveDataToServer();

    super.saveData(data);
  }

  /**
   * Return the next step from a step list
   * @param stepList
   * @param formConfig
   */
  getNextStepFromList(stepList: Step[], formConfig: FormConfig = null): any {
      // set all data again in the form to avoid using old data for toggle fields
      if(this.anfrage?.bestandsanalyseInput && this.formConfig) {
          this.formConfig.updateFields(this.anfrage.bestandsanalyseInput);
          // save the data again. In some case some fields are autoupdatetd
          this.saveData({});
      }

      return super.getNextStepFromList(stepList, formConfig);
  }

  /**
   * moveon to the next step in the routing
   */
  moveToNextStep(): void {
    // set marker to recalculate the measures.
    this.anfrage.recalculateMeasures = true;

    // get the next step that either has no fields or at least one active field
    let nextStep = this.getNextStepFromList(this.steps.nextStepList);

    if(!nextStep) {
      nextStep = {path: this.portalService.currentPortalId + '/moderat/sanierung/massnahmen'};
    }
    else if(nextStep.name === 'energy-map') {
      if(this.energyMapService.showEnergyMap(this.anfrage, 'moderat') === false
        || this.energyMapService.isEnergyMapDataDonated(this.anfrage) === true
        || this.energyMapService.getEnergyMapConfirmDonation(this.anfrage) === 'n'
        || this.energyMapService.getEnergyMapBuildingNotIncludedInMap(this.anfrage) === true) {
        // If the user has donated the data or declined the donation, the EnergyMap page will no longer be displayed.
        nextStep = {path: this.portalService.currentPortalId + '/moderat/sanierung/massnahmen'};
      }
    }

    this.moved = true;
    // move on to the next step
    this.router.navigateByUrl(nextStep.path);
  }

  /**
   * @inheritDoc
   */
  onStepUpdate() {
    super.onStepUpdate();

    // change the pagehelptext for some cases here
    if(this.steps.currentStep.name === 'daemmung/3' && !(this.anfrage?.bestandsanalyseInput?.gebaeude_dachform === 'spitzdach' &&
      this.anfrage?.bestandsanalyseInput?.gebaeude_dachausbau === 'nein')) {
      this.helpTextkey += '.dach';
    }

    // special case: on this step, if we move backward, we change the formelement.
    // Thefore the last step must be determined with the formconfig fromm ths previous steps
    if(this.steps.currentStep.name === 'wpcheizanlage/1' && this.anfrage?.verbrauchsanalyseInput) {
      const formConfig = new VerbrauchsanalyseModeratForm(this.energyMapService);
      formConfig.getForm(this.steps.currentStep.name);
      formConfig.setData(this.anfrage.verbrauchsanalyseInput);

      this.previousStep = this.getNextStepFromList(this.steps.previousStepList, formConfig);
    }
  }

  /**
   * @inheritDoc
   */
  onSubmit() {
    super.onSubmit();

    if(this.form.valid === false) {
      // form is not valid, maybe form errors are shown. Scroll upwards
      this.elementService.scrollToElement(this.stepForm.nativeElement);
    }
  }
}
